import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  PublicHoliday,
  PublicHolidayCreate,
  PublicHolidayUpdate,
  Religion,
} from '@lms/shared-types'
import { ApiError } from '../api/client'
import {
  createPublicHoliday,
  deletePublicHoliday,
  listAdminPublicHolidays,
  updatePublicHoliday,
} from '../api/public-holidays'
import { Card } from '../components/primitives/Card'
import { ConfirmationDialog } from '../components/primitives/ConfirmationDialog'
import { DateField } from '../components/primitives/DateField'
import { FadeIn } from '../components/primitives/FadeIn'
import { Field } from '../components/primitives/Field'
import { Modal } from '../components/primitives/Modal'
import { PageHead } from '../components/primitives/PageHead'
import { ThinkingDots } from '../components/primitives/ThinkingDots'
import { HrAccessGuard } from '../components/hr/HrAccessGuard'
import { fmtDateWithWeekday } from '../lib/format'
import { getOrgConfig } from '../api/org-config'

const RELIGION_OPTIONS: ReadonlyArray<{ value: Religion | ''; label: string }> = [
  { value: '', label: '— none —' },
  { value: 'islam', label: 'Islam' },
  { value: 'buddhist', label: 'Buddhist' },
  { value: 'hindu', label: 'Hindu' },
  { value: 'christian', label: 'Christian' },
  { value: 'other', label: 'Other' },
]

const REGION_HINT =
  'Use ISO-style codes (e.g. MY-FED for federal, MY-SEL for Selangor).'

interface DraftHoliday {
  date: string
  region: string
  name: string
  tentative: boolean
  religion: Religion | ''
}

function toDraft(row: PublicHoliday): DraftHoliday {
  return {
    date: row.date,
    region: row.region,
    name: row.name,
    tentative: row.tentative,
    religion: row.religion ?? '',
  }
}

function emptyDraft(defaultRegion: string): DraftHoliday {
  return {
    date: '',
    region: defaultRegion,
    name: '',
    tentative: false,
    religion: '',
  }
}

function toCreate(draft: DraftHoliday): PublicHolidayCreate {
  return {
    date: draft.date,
    region: draft.region,
    name: draft.name,
    tentative: draft.tentative,
    religion: draft.religion === '' ? null : draft.religion,
  }
}

function toUpdate(draft: DraftHoliday): PublicHolidayUpdate {
  return {
    name: draft.name,
    tentative: draft.tentative,
    religion: draft.religion === '' ? null : draft.religion,
  }
}

function formatDateLong(iso: string): string {
  return fmtDateWithWeekday(iso)
}

function fallsOnWeekend(iso: string): boolean {
  // ISO date 'YYYY-MM-DD' parsed as local midnight to avoid TZ shift.
  const [y, m, d] = iso.split('-').map((s) => parseInt(s, 10))
  if (!y || !m || !d) return false
  const day = new Date(y, m - 1, d).getDay()
  return day === 0 || day === 6
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return `${err.message} (${err.status})`
  if (err instanceof Error) return err.message
  return 'Unexpected error'
}

export function HrHolidaysPage(): JSX.Element {
  const queryClient = useQueryClient()
  const [creating, setCreating] = useState<DraftHoliday | null>(null)
  const [editing, setEditing] = useState<PublicHoliday | null>(null)
  const [editDraft, setEditDraft] = useState<DraftHoliday | null>(null)
  const [pendingDelete, setPendingDelete] = useState<PublicHoliday | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Malaysia has ~15-20 public holidays a year; even spanning 2025 Q4 +
  // 2026 + 2027 Q1 the list is ~35 rows. It's a
  // bounded, chronological admin table, so we show the whole year at once
  // rather than paginating — pagination here only hid the later months
  // (e.g. Deepavali in November) behind a window the page never advanced.
  const { data: allHolidays = [], isLoading } = useQuery({
    queryKey: ['public-holidays', 'admin'],
    queryFn: listAdminPublicHolidays,
  })
  const { data: orgConfig } = useQuery({
    queryKey: ['org-config', 'full'],
    queryFn: getOrgConfig,
  })
  const defaultRegion = orgConfig?.localeRegion ?? 'MY-FED'

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['public-holidays'] })
    void queryClient.invalidateQueries({ queryKey: ['calendar'] })
  }

  const createMutation = useMutation({
    mutationFn: (body: PublicHolidayCreate) => createPublicHoliday(body),
    onSuccess: () => {
      setCreating(null)
      setSubmitError(null)
      invalidate()
    },
    onError: (err) => setSubmitError(errorMessage(err)),
  })

  const updateMutation = useMutation({
    mutationFn: ({
      date,
      region,
      body,
    }: {
      date: string
      region: string
      body: PublicHolidayUpdate
    }) => updatePublicHoliday(date, region, body),
    onSuccess: () => {
      setEditing(null)
      setEditDraft(null)
      setSubmitError(null)
      invalidate()
    },
    onError: (err) => setSubmitError(errorMessage(err)),
  })

  const deleteMutation = useMutation({
    mutationFn: ({ date, region }: { date: string; region: string }) =>
      deletePublicHoliday(date, region),
    onSuccess: () => {
      setPendingDelete(null)
      invalidate()
    },
  })

  return (
    <HrAccessGuard>
      <div className="page">
      <PageHead
        eyebrow="HR"
        title="Public holidays"
        subtitle="Federal and state holidays observed by the company. CRUD writes are audit-logged."
        actions={
          <button
            type="button"
            data-testid="new-holiday"
            onClick={() => {
              setCreating(emptyDraft(defaultRegion))
              setSubmitError(null)
            }}
            style={{
              padding: '8px 14px',
              borderRadius: 10,
              border: 'none',
              background: 'var(--steel-blue)',
              color: 'var(--surface)',
              fontWeight: 600,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            + New holiday
          </button>
        }
      />

      <Card padding={0} style={{ overflow: 'hidden' }}>
        {isLoading ? (
          <div style={{ padding: 32, textAlign: 'center' }}>
            <ThinkingDots label="Loading holidays" />
          </div>
        ) : allHolidays.length === 0 ? (
          <FadeIn>
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>
              No public holidays configured.
            </div>
          </FadeIn>
        ) : (
          <table
            data-testid="holidays-table"
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 14,
            }}
          >
            <thead>
              <tr style={{ background: 'var(--surface-2)' }}>
                <Th>Date</Th>
                <Th>Name</Th>
                <Th>Region</Th>
                <Th>Religion</Th>
                <Th>Tentative</Th>
                <Th style={{ textAlign: 'right' }}>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {allHolidays.map((h) => (
                <tr
                  key={`${h.date}-${h.region}`}
                  data-testid="holiday-row"
                  style={{ borderTop: '1px solid var(--border)' }}
                >
                  <Td>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {formatDateLong(h.date)}
                    </span>
                    {fallsOnWeekend(h.date) ? (
                      <span
                        data-testid="weekend-badge"
                        title="Falls on a weekend. If a replacement day is gazetted, log it as its own holiday."
                        style={{
                          ...weekendBadgeStyle,
                          background: 'var(--surface-2)',
                          color: 'var(--text-3)',
                        }}
                      >
                        Weekend
                      </span>
                    ) : null}
                  </Td>
                  <Td>
                    {h.name}
                  </Td>
                  <Td>
                    <span
                      style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: 12,
                        background: 'var(--surface-2)',
                        padding: '2px 6px',
                        borderRadius: 4,
                      }}
                    >
                      {h.region}
                    </span>
                  </Td>
                  <Td>
                    {h.religion ? (
                      <span style={{ color: 'var(--text-2)' }}>{h.religion}</span>
                    ) : (
                      <span style={{ color: 'var(--border-strong)' }}>—</span>
                    )}
                  </Td>
                  <Td>
                    {h.tentative ? (
                      <span
                        style={{
                          color: 'var(--warning)',
                          fontWeight: 600,
                          fontSize: 12,
                        }}
                      >
                        Tentative
                      </span>
                    ) : (
                      <span style={{ color: 'var(--border-strong)' }}>—</span>
                    )}
                  </Td>
                  <Td style={{ textAlign: 'right' }}>
                    <button
                      type="button"
                      data-testid="edit-holiday"
                      onClick={() => {
                        setEditing(h)
                        setEditDraft(toDraft(h))
                        setSubmitError(null)
                      }}
                      style={rowButtonStyle}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      data-testid="delete-holiday"
                      onClick={() => setPendingDelete(h)}
                      style={{ ...rowButtonStyle, color: 'var(--danger)' }}
                    >
                      Delete
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* ── Create modal ─────────────────────────────────────────── */}
      <Modal
        open={creating !== null}
        onClose={() => {
          setCreating(null)
          setSubmitError(null)
        }}
        title="New public holiday"
        footer={
          <div style={modalFooterStyle}>
            <button
              type="button"
              onClick={() => setCreating(null)}
              style={secondaryButtonStyle}
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="save-new-holiday"
              disabled={
                creating === null ||
                creating.date === '' ||
                creating.name.trim() === '' ||
                createMutation.isPending
              }
              onClick={() => {
                if (creating !== null) createMutation.mutate(toCreate(creating))
              }}
              style={primaryButtonStyle}
            >
              {createMutation.isPending ? 'Creating…' : 'Create'}
            </button>
          </div>
        }
      >
        {creating !== null && (
          <HolidayForm
            draft={creating}
            onChange={setCreating}
            error={submitError}
          />
        )}
      </Modal>

      {/* ── Edit modal ───────────────────────────────────────────── */}
      <Modal
        open={editing !== null && editDraft !== null}
        onClose={() => {
          setEditing(null)
          setEditDraft(null)
          setSubmitError(null)
        }}
        title={editing ? `Edit ${editing.name}` : 'Edit holiday'}
        footer={
          <div style={modalFooterStyle}>
            <button
              type="button"
              onClick={() => {
                setEditing(null)
                setEditDraft(null)
              }}
              style={secondaryButtonStyle}
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="save-edited-holiday"
              disabled={
                editDraft === null ||
                editDraft.name.trim() === '' ||
                updateMutation.isPending
              }
              onClick={() => {
                if (editing !== null && editDraft !== null) {
                  updateMutation.mutate({
                    date: editing.date,
                    region: editing.region,
                    body: toUpdate(editDraft),
                  })
                }
              }}
              style={primaryButtonStyle}
            >
              {updateMutation.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        }
      >
        {editDraft !== null && (
          <HolidayForm
            draft={editDraft}
            onChange={setEditDraft}
            keyLocked
            error={submitError}
          />
        )}
      </Modal>

      {/* ── Delete confirmation ──────────────────────────────────── */}
      <ConfirmationDialog
        open={pendingDelete !== null}
        title="Delete holiday?"
        confirmLabel="Delete"
        variant="danger"
        loading={deleteMutation.isPending}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete) {
            deleteMutation.mutate({
              date: pendingDelete.date,
              region: pendingDelete.region,
            })
          }
        }}
      >
        {pendingDelete && (
          <p style={{ margin: 0, color: 'var(--text-2)', fontSize: 14 }}>
            Remove <strong>{pendingDelete.name}</strong> on{' '}
            {formatDateLong(pendingDelete.date)} ({pendingDelete.region})? The
            audit log will record the removal.
          </p>
        )}
      </ConfirmationDialog>
      </div>
    </HrAccessGuard>
  )
}

// ── Form ─────────────────────────────────────────────────────────────────────

interface HolidayFormProps {
  draft: DraftHoliday
  onChange: (next: DraftHoliday) => void
  /** When true, date + region inputs are read-only (edit mode — PK
   *  is not patchable). */
  keyLocked?: boolean
  error: string | null
}

function HolidayForm({
  draft,
  onChange,
  keyLocked = false,
  error,
}: HolidayFormProps): JSX.Element {
  const update = <K extends keyof DraftHoliday>(
    key: K,
    value: DraftHoliday[K],
  ): void => {
    onChange({ ...draft, [key]: value })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Field label="Date" required>
        <DateField
          value={draft.date || null}
          onChange={(iso) => update('date', iso ?? '')}
          disabled={keyLocked}
        />
      </Field>

      <Field label="Region" required helperText={REGION_HINT}>
        <input
          type="text"
          maxLength={16}
          value={draft.region}
          onChange={(e) => update('region', e.target.value)}
          disabled={keyLocked}
          style={inputStyle}
        />
      </Field>

      <Field label="Name" required>
        <input
          type="text"
          maxLength={200}
          value={draft.name}
          onChange={(e) => update('name', e.target.value)}
          style={inputStyle}
        />
      </Field>

      <Field
        label="Religion"
        helperText="Set for religion-specific holidays (e.g. Hari Raya — Islam)."
      >
        <select
          value={draft.religion}
          onChange={(e) => update('religion', e.target.value as Religion | '')}
          style={inputStyle}
        >
          {RELIGION_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </Field>

      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 14,
          color: 'var(--text)',
        }}
      >
        <input
          type="checkbox"
          checked={draft.tentative}
          onChange={(e) => update('tentative', e.target.checked)}
        />
        <span>
          Tentative — the date is officially announced later (typically Hari
          Raya).
        </span>
      </label>

      {error && (
        <div
          role="alert"
          style={{
            color: 'var(--danger)',
            background: 'var(--danger-tint)',
            padding: '8px 12px',
            borderRadius: 8,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}
    </div>
  )
}

// ── Styles ───────────────────────────────────────────────────────────────────

function Th({
  children,
  style,
}: {
  children: React.ReactNode
  style?: React.CSSProperties
}): JSX.Element {
  return (
    <th
      style={{
        padding: '12px 16px',
        textAlign: 'left',
        fontSize: 12,
        fontWeight: 600,
        color: 'var(--text-2)',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        ...style,
      }}
    >
      {children}
    </th>
  )
}

function Td({
  children,
  style,
}: {
  children: React.ReactNode
  style?: React.CSSProperties
}): JSX.Element {
  return (
    <td style={{ padding: '12px 16px', color: 'var(--text)', ...style }}>
      {children}
    </td>
  )
}

const weekendBadgeStyle: React.CSSProperties = {
  marginLeft: 8,
  padding: '2px 6px',
  borderRadius: 4,
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 0.3,
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
}

const rowButtonStyle: React.CSSProperties = {
  marginLeft: 8,
  padding: '4px 10px',
  borderRadius: 6,
  border: '1px solid var(--border)',
  background: 'transparent',
  color: 'var(--text-2)',
  fontSize: 12,
  fontWeight: 500,
  cursor: 'pointer',
}

const inputStyle: React.CSSProperties = {
  padding: '8px 12px',
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: 14,
  fontFamily: 'inherit',
}

const primaryButtonStyle: React.CSSProperties = {
  padding: '8px 16px',
  borderRadius: 10,
  border: 'none',
  background: 'var(--steel-blue)',
  color: 'var(--surface)',
  fontWeight: 600,
  fontSize: 14,
  cursor: 'pointer',
}

const secondaryButtonStyle: React.CSSProperties = {
  padding: '8px 16px',
  borderRadius: 10,
  border: '1px solid var(--border)',
  background: 'transparent',
  color: 'var(--text)',
  fontSize: 14,
  cursor: 'pointer',
}

const modalFooterStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 8,
}
